import { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import Select from '../Select/Select';
import './Table.scss';

// Rovnaké ako $breakpoint-md v styles/_variables.scss
const MOBILE_QUERY = '(max-width: 767px)';
// Tabuľka so šírkami stĺpcov (table-layout: fixed) nikdy nepretečie, len oreže text –
// karty sa zapnú, keď by na stĺpec pripadlo menej ako toľkoto px
const MIN_FIXED_COLUMN_WIDTH = 120;

function Table({
  columns = [],
  data = [],
  searchable = true,
  sortable = true,
  paginated = true,
  selectable = false,
  pageSize: initialPageSize = 10,
  pageSizeOptions = [5, 10, 20, 50],
  searchPlaceholder = 'Hľadať...',
  emptyMessage = 'Žiadne dáta',
  onSelectionChange,
  className = '',
  maxHeight = '70vh',
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selectedRows, setSelectedRows] = useState(new Set());

  // --- Search / Filter ---
  const filteredData = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        const val = row[col.key];
        return val != null && String(val).toLowerCase().includes(q);
      })
    );
  }, [data, search, columns]);

  // --- Sort ---
  const sortedData = useMemo(() => {
    if (!sortKey) return filteredData;
    const col = columns.find((c) => c.key === sortKey);
    const compareFn = col?.sortFn;
    return [...filteredData].sort((a, b) => {
      let result;
      if (compareFn) {
        result = compareFn(a[sortKey], b[sortKey]);
      } else {
        const aVal = a[sortKey] ?? '';
        const bVal = b[sortKey] ?? '';
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          result = aVal - bVal;
        } else {
          result = String(aVal).localeCompare(String(bVal));
        }
      }
      return sortDir === 'asc' ? result : -result;
    });
  }, [filteredData, sortKey, sortDir, columns]);

  // --- Pagination ---
  const totalPages = paginated ? Math.max(1, Math.ceil(sortedData.length / pageSize)) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const paginatedData = paginated
    ? sortedData.slice((safePage - 1) * pageSize, safePage * pageSize)
    : sortedData;

  const startIndex = paginated ? (safePage - 1) * pageSize + 1 : 1;
  const endIndex = paginated ? Math.min(safePage * pageSize, sortedData.length) : sortedData.length;

  // --- Responzivita: riadky ako karty na mobile, alebo keď sa stĺpce nezmestia na šírku ---
  const scrollRef = useRef(null);
  const tableRef = useRef(null);
  const neededWidthRef = useRef(0);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  const [overflows, setOverflows] = useState(false);
  const cardView = isMobile || overflows;
  const fixedLayout = columns.some((col) => col.width);

  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const handleChange = (e) => setIsMobile(e.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  // Iný obsah môže potrebovať inú šírku – znova sa zmeria v tabuľkovom zobrazení
  const contentKey = `${columns.length}|${sortedData.length}|${safePage}|${pageSize}`;
  useLayoutEffect(() => {
    setOverflows(false);
  }, [contentKey]);

  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll || isMobile) return;
    const check = () => {
      if (!overflows) {
        // Šírka tabuľky sa dá zmerať, len kým sa zobrazuje ako tabuľka
        const needed = Math.max(
          tableRef.current.offsetWidth,
          fixedLayout ? columns.length * MIN_FIXED_COLUMN_WIDTH : 0
        );
        if (needed > scroll.clientWidth + 1) {
          // + šírka zvislého scrollbaru (karty ho nemajú) – inak by sa zobrazenie na hranici preklápalo
          neededWidthRef.current = needed + (scroll.offsetWidth - scroll.clientWidth);
          setOverflows(true);
        }
      } else if (scroll.offsetWidth >= neededWidthRef.current) {
        setOverflows(false);
      }
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(scroll);
    return () => observer.disconnect();
  }, [isMobile, overflows, contentKey, fixedLayout, columns.length]);

  // --- Handlers ---
  const handleSort = (key) => {
    if (!sortable) return;
    const col = columns.find((c) => c.key === key);
    if (col?.sortable === false) return;
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
      onSelectionChange?.([]);
    } else {
      const newSet = new Set(paginatedData.map((_, i) => (safePage - 1) * pageSize + i));
      setSelectedRows(newSet);
      onSelectionChange?.(paginatedData);
    }
  };

  const handleSelectRow = (globalIndex) => {
    const newSet = new Set(selectedRows);
    if (newSet.has(globalIndex)) {
      newSet.delete(globalIndex);
    } else {
      newSet.add(globalIndex);
    }
    setSelectedRows(newSet);
    onSelectionChange?.(sortedData.filter((_, i) => newSet.has(i)));
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  // --- Page buttons ---
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className={`table-wrapper ${cardView ? 'table-wrapper--cards' : ''} ${className}`}>
      {/* Toolbar */}
      {(searchable || paginated) && (
        <div className="table-wrapper__toolbar">
          {searchable && (
            <div className="table-wrapper__search">
              <span className="table-wrapper__search-icon">🔍</span>
              <input
                type="text"
                className="table-wrapper__search-input"
                placeholder={searchPlaceholder}
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          )}
          <div className="table-wrapper__toolbar-actions">
            {paginated && (
              <div className="table-wrapper__page-size">
                <span>Zobraz</span>
                <Select
                  value={pageSize}
                  onChange={(val) => {
                    setPageSize(Number(val));
                    setCurrentPage(1);
                  }}
                  options={pageSizeOptions.map((opt) => ({
                    value: opt,
                    label: String(opt),
                  }))}
                />
                <span>riadkov</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div
        ref={scrollRef}
        className="table-wrapper__scroll"
        style={cardView ? undefined : { maxHeight }}
      >
        <table ref={tableRef} className={`table-wrapper__table ${fixedLayout ? 'table-wrapper__table--fixed' : ''}`}>
          <thead className="table-wrapper__thead">
            <tr>
              {selectable && (
                <th className="table-wrapper__th" style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    className="table-wrapper__checkbox"
                    checked={paginatedData.length > 0 && selectedRows.size === paginatedData.length}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              {columns.map((col) => {
                const isSortable = sortable && col.sortable !== false;
                const isActive = sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    className={`table-wrapper__th ${isSortable ? 'table-wrapper__th--sortable' : ''} ${isActive ? 'table-wrapper__th--active' : ''}`}
                    style={col.width ? { width: col.width } : undefined}
                    onClick={() => isSortable && handleSort(col.key)}
                  >
                    {col.label}
                    {isSortable && (
                      <span className="table-wrapper__sort-icon">
                        {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td className="table-wrapper__empty" colSpan={columns.length + (selectable ? 1 : 0)}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, i) => {
                const globalIndex = (safePage - 1) * pageSize + i;
                const isSelected = selectedRows.has(globalIndex);
                return (
                  <tr
                    key={row.id ?? globalIndex}
                    className={`table-wrapper__tr ${isSelected ? 'table-wrapper__tr--selected' : ''}`}
                  >
                    {selectable && (
                      <td className="table-wrapper__td">
                        <input
                          type="checkbox"
                          className="table-wrapper__checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(globalIndex)}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key} className="table-wrapper__td" data-label={col.label}>
                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / Pagination */}
      {paginated && sortedData.length > 0 && (
        <div className="table-wrapper__footer">
          <span className="table-wrapper__info">
            {startIndex}–{endIndex} z {sortedData.length}
          </span>
          <div className="table-wrapper__pagination">
            <button
              className="table-wrapper__page-btn"
              disabled={safePage <= 1}
              onClick={() => setCurrentPage(1)}
            >
              «
            </button>
            <button
              className="table-wrapper__page-btn"
              disabled={safePage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              ‹
            </button>
            {getPageNumbers().map((num) => (
              <button
                key={num}
                className={`table-wrapper__page-btn ${num === safePage ? 'table-wrapper__page-btn--active' : ''}`}
                onClick={() => setCurrentPage(num)}
              >
                {num}
              </button>
            ))}
            <button
              className="table-wrapper__page-btn"
              disabled={safePage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              ›
            </button>
            <button
              className="table-wrapper__page-btn"
              disabled={safePage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Table;
